import { useState, SetStateAction, Dispatch } from "react";
import { Dialog, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSaveComment, useDeleteComment } from "src/api/client-api/property/useManageAttachment";
import { format, parseISO } from "date-fns";
import { CommentDialogHeader } from "./CommentDialogHeader";
import { CommentDialogActions } from "./CommentDialogActions";
import { CommentDialogContent } from "./CommentDialogContent";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";

interface CommentDialogProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    ssqPostElementId: number;
    commentData: CommentData | null;
    onCommentChanged?: () => void;
}

export type CommentData = {
    comment: string;
    commentId: string;
    updatedAt: string;
    userName: string;
};

export const CommentDialog = (props: CommentDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { open, setOpen, ssqPostElementId, commentData, onCommentChanged } = props;
    const { comment, commentId, updatedAt, userName } = commentData ?? {};
    const updatedAtFormatted = updatedAt ? format(parseISO(updatedAt), "dd/MM/yyyy, HH:mm") : null;

    const [newComment, setNewComment] = useState(comment ?? "");
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const saveComment = useSaveComment();
    const deleteComment = useDeleteComment();

    const handleCancel = () => {
        setNewComment(comment ?? "");
        setOpen(false);
    };

    const handleSave = () => {
        saveComment.mutate(
            { comment: newComment, ssqPostElementId },
            {
                onSuccess: () => {
                    if (onCommentChanged) onCommentChanged();
                    setOpen(false);
                },
            },
        );
    };

    const handleDeleteAndSave = () => {
        if (!commentId) return;
        deleteComment.mutate(commentId, {
            onSuccess: () => handleSave(),
        });
    };

    const handleDelete = () => {
        if (!commentId) return;
        deleteComment.mutate(commentId, {
            onSuccess: () => {
                if (onCommentChanged) onCommentChanged();
                setNewComment("");
                setOpen(false);
            },
        });
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{ borderRadius: theme.spacing(40) }}
            >
                <CommentDialogHeader
                    title={commentId ? t("property.note") : t("property.note.add_note")}
                    onClose={() => setOpen(false)}
                />

                <CommentDialogContent
                    newComment={newComment}
                    onChange={setNewComment}
                    placeholder={t("property.note.placeholder")}
                    updatedAtFormatted={
                        updatedAtFormatted ? `${t("property.note.last_edited")} ${updatedAtFormatted}` : null
                    }
                    userName={userName}
                />

                <CommentDialogActions
                    commentId={commentId}
                    comment={comment}
                    newComment={newComment}
                    onCancel={handleCancel}
                    onDeleteClick={() => setConfirmDeleteOpen(true)}
                    onSave={handleSave}
                    onDeleteAndSave={handleDeleteAndSave}
                />
            </Dialog>

            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={() => {
                    handleDelete();
                    setConfirmDeleteOpen(false);
                }}
            />
        </>
    );
};
